import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { ACTIVITY_TYPES, MOCK_TASKS } from '../plannerMockData';
import { WEEKDAY_LABELS, dateFromDayAndMinutes, minutesFromDate, formatDuration, getDayKey } from '../plannerUtils';

// Common durations offered in the picker; the current value is added on top when it doesn't match one of these.
const DURATION_PRESETS_MIN = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 420, 480];

const ActivityFormDialog = ({ open, onClose, onSave, onDelete, onDuplicate, initialValues, weekDates }) => {
  const [type, setType] = useState('Task');
  const [taskId, setTaskId] = useState('');
  const [day, setDay] = useState(getDayKey(weekDates[0]));
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const v = initialValues || {};
    setType(v.type || 'Task');
    setTaskId(v.taskId || '');
    const activeDay = v.day || getDayKey(weekDates[0]);
    setDay(activeDay);
    setStartTime(dateFromDayAndMinutes(activeDay, v.startMinutes ?? 9 * 60));
    setEndTime(dateFromDayAndMinutes(activeDay, v.endMinutes ?? 10 * 60));
    setNotes(v.notes || '');
    setError('');
  }, [open, initialValues, weekDates]);

  const isEditing = Boolean(initialValues && initialValues.id);
  const durationMinutes = startTime && endTime ? minutesFromDate(endTime) - minutesFromDate(startTime) : 0;
  const isCustomDuration = durationMinutes > 0 && !DURATION_PRESETS_MIN.includes(durationMinutes);

  // Changing the start time keeps the current duration and slides the end time along with it.
  const handleStartTimeChange = (newStart) => {
    const previousDuration = durationMinutes;
    setStartTime(newStart);
    if (newStart && previousDuration > 0) {
      setEndTime(new Date(newStart.getTime() + previousDuration * 60000));
    }
  };

  // Picking a duration derives the end time from the current start time.
  const handleDurationChange = (minutes) => {
    if (!startTime) return;
    setEndTime(new Date(startTime.getTime() + minutes * 60000));
  };

  const handleSave = () => {
    if (!startTime || !endTime || minutesFromDate(endTime) <= minutesFromDate(startTime)) {
      setError('End time must be after start time.');
      return;
    }
    if (type === 'Task' && !taskId) {
      setError('Please select a task.');
      return;
    }

    onSave({
      id: initialValues?.id,
      type,
      taskId: type === 'Task' ? taskId : null,
      day,
      startMinutes: minutesFromDate(startTime),
      endMinutes: minutesFromDate(endTime),
      notes,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEditing ? 'Edit Activity' : 'Add Activity'}
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="activity-type-label">Activity Type</InputLabel>
                <Select
                  labelId="activity-type-label"
                  label="Activity Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="activity-day-label">Date</InputLabel>
                <Select
                  labelId="activity-day-label"
                  label="Date"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                >
                  {weekDates.map((d, i) => (
                    <MenuItem key={getDayKey(d)} value={getDayKey(d)}>
                      {WEEKDAY_LABELS[i]} ({d.getDate()}/{d.getMonth() + 1})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {type === 'Task' && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="activity-task-label">Task</InputLabel>
                  <Select
                    labelId="activity-task-label"
                    label="Task"
                    value={taskId}
                    onChange={(e) => setTaskId(e.target.value)}
                  >
                    {MOCK_TASKS.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={handleStartTimeChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="activity-duration-label">Duration</InputLabel>
                <Select
                  labelId="activity-duration-label"
                  label="Duration"
                  value={durationMinutes > 0 ? durationMinutes : ''}
                  onChange={(e) => handleDurationChange(Number(e.target.value))}
                  disabled={!startTime}
                >
                  {isCustomDuration && (
                    <MenuItem value={durationMinutes}>{formatDuration(durationMinutes)}</MenuItem>
                  )}
                  {DURATION_PRESETS_MIN.map((m) => (
                    <MenuItem key={m} value={m}>
                      {formatDuration(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="Optional notes"
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: isEditing ? 'space-between' : 'flex-end' }}>
        {isEditing && (
          <Box>
            <Button color="error" onClick={() => onDelete(initialValues.id)} sx={{ mr: 1 }}>
              Delete
            </Button>
            <Button startIcon={<ContentCopyIcon />} onClick={() => onDuplicate(initialValues)}>
              Duplicate
            </Button>
          </Box>
        )}
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityFormDialog;
